import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Breadcrumbs from "../../../components/Common/Breadcrumb";

import { fetchTryout } from '../../../services/tryoutService';

const DaftarTryout = () => {
  document.title = "Kelola Soal Tryout";

  const [tryouts, setTryouts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [loadingTryouts, setLoadingTryouts] = useState(false);

  // States for filters
  const [selectedTipe, setSelectedTipe] = useState(''); // Initial value '' for 'Semua Tipe'
  const [selectedJenisSeleksi, setSelectedJenisSeleksi] = useState(''); // Initial value '' for 'Semua Jenis Seleksi'

  // Function to fetch tryouts with filters
  const fetchTryouts = async (page = 1) => {
    try {
      setLoadingTryouts(true);
      const filterParams = {};

      if (selectedTipe !== '') {
        filterParams.tipe = selectedTipe;
      }
      if (selectedJenisSeleksi !== '') {
        filterParams.jenis_seleksi = selectedJenisSeleksi;
      }

      const response = await fetchTryout(page, pagination.per_page, filterParams);

      if (!response || !response.success) {
        throw new Error(response?.message || "No response from server");
      }

      const tryoutData = response.data?.data || response.data || [];
      const metaData = response.meta || {
        page: 1,
        per_page: pagination.per_page,
        total: tryoutData.length
      };

      setTryouts(Array.isArray(tryoutData) ? tryoutData : [tryoutData]);
      setPagination({
        page: metaData.page,
        per_page: metaData.per_page,
        total: metaData.total
      });

    } catch (error) {
      console.error("Error fetching tryouts:", error);
      setTryouts([]);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0
      }));
      toast.error(error.message || "Error loading tryout data");
    } finally {
      setLoadingTryouts(false);
    }
  };

  // Effect to fetch initial tryouts and re-fetch when pagination or filters change
  useEffect(() => {
    fetchTryouts(pagination.page);
  }, [pagination.page, pagination.per_page, selectedTipe, selectedJenisSeleksi]);

  // Handle filter changes for Tipe
  const handleTipeChange = (e) => {
    setSelectedTipe(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle filter changes for Jenis Seleksi
  const handleJenisSeleksiChange = (e) => {
    setSelectedJenisSeleksi(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // New function to reset all filters
  const handleResetFilters = () => {
    setSelectedTipe(''); // Reset tipe to default
    setSelectedJenisSeleksi(''); // Reset jenis seleksi to default
    setPagination(prev => ({ ...prev, page: 1 })); // Reset pagination to first page
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Tryout" breadcrumbItem="Kelola Soal Tryout" />

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <CardTitle>Pilih Tryout untuk Mengelola Soal</CardTitle>
                  <p className="card-title-desc mb-4">
                    Pilih tryout dari daftar di bawah untuk mulai **menambahkan atau mengelola soal-soal** berdasarkan list tryout.
                  </p>

                  {/* Filter Section */}
                  <Row className="mb-4 align-items-end"> {/* Use align-items-end to vertically align items */}
                    <Col md={4}>
                      <div className="mb-3">
                        <Label htmlFor="filterJenisSeleksi">Filter Jenis Seleksi:</Label>
                        <Input
                          type="select"
                          id="filterJenisSeleksi"
                          name="jenis_seleksi"
                          value={selectedJenisSeleksi}
                          onChange={handleJenisSeleksiChange}
                          className="form-select"
                        >
                          <option value="">Semua Jenis Seleksi</option>
                          <option value="CPNS">CPNS</option>
                          <option value="PPPK">PPPK</option>
                          <option value="KEDINASAN">KEDINASAN</option>
                          <option value="TNI">TNI</option>
                          <option value="POLRI">POLRI</option>
                          <option value="SPPI">SPPI</option>
                        </Input>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label htmlFor="filterTipe">Filter Tipe:</Label>
                        <Input
                          type="select"
                          id="filterTipe"
                          name="tipe"
                          value={selectedTipe}
                          onChange={handleTipeChange}
                          className="form-select"
                        >
                          <option value="">Semua Tipe</option>
                          <option value="GRATIS">Gratis</option>
                          <option value="PREMIUM">Premium</option>
                        </Input>
                      </div>
                    </Col>
                    <Col md={4}>
                      {/* Reset Button */}
                      <div className="mb-3">
                        <Button color="danger" onClick={handleResetFilters} className="w-50">
                          Reset Filter
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {/* End Filter Section */}

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Label htmlFor="perPageSelect" className="mb-0">Items per page:</Label>
                    <Input
                      id="perPageSelect"
                      type="select"
                      style={{ width: '80px' }}
                      value={pagination.per_page}
                      onChange={(e) => setPagination(prev => ({
                        ...prev,
                        per_page: Number(e.target.value),
                        page: 1
                      }))}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Input>
                  </div>

                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Jenis Seleksi</th>
                          <th>Nama Tryout</th>
                          <th>Tipe</th>
                          <th>Durasi (menit)</th>
                          <th>Bidang</th>
                          <th>Tanggal Dibuat</th>
                          <th>Kelola Soal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingTryouts ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : tryouts.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              Tidak ada data Tryout yang ditemukan.
                            </td>
                          </tr>
                        ) : (
                          tryouts.map((tryout, index) => (
                            <tr key={tryout.id_tryout}>
                              <th scope="row">{(pagination.page - 1) * pagination.per_page + index + 1}</th>
                              <td>{tryout.jenis_seleksi}</td>
                              <td>{tryout.nama_tryout}</td>
                              <td>
                                <span
                                  className={`badge ${tryout.tipe === 'GRATIS' ? 'bg-success' : 'bg-primary'}`}
                                >
                                  {tryout.tipe}
                                </span>
                              </td>
                              <td>{tryout.durasi_menit}</td>
                              <td>{tryout.nama_bidang}</td>
                              <td>{new Date(tryout.created_at).toISOString().slice(0, 10)}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Link to={`/soal-list?id_tryout=${tryout.id_tryout}`}>
                                    <Button color="success" size="sm">
                                      Tambah Soal
                                    </Button>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span className="text-muted">
                        Showing {(pagination.page - 1) * pagination.per_page + 1} to{' '}
                        {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                        {pagination.total} entries
                      </span>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        Previous
                      </Button>

                      {Array.from({ length: Math.ceil(pagination.total / pagination.per_page) }, (_, i) => i + 1)
                        .slice(
                          Math.max(0, pagination.page - 3),
                          Math.min(Math.ceil(pagination.total / pagination.per_page), pagination.page + 2)
                        )
                        .map(page => (
                          <Button
                            key={page}
                            color={pagination.page === page ? "primary" : "light"}
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page }))}
                          >
                            {page}
                          </Button>
                        ))
                      }

                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page * pagination.per_page >= pagination.total}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default DaftarTryout;